import { useCallback } from 'react';
import { createResource, type RouteResource } from '@atlassian/react-resource-router';
import type { PreferenceOptions, UserPreferences } from './UserPreferences.types.tsx';
import {
	DEFAULT_EDITOR_PREFERENCE_KEY,
	WORKFLOW_USER_PREFERENCES_KEY,
	CHANGEBOARDING_DISMISSED_PREFERENCE_KEY,
	TEXT_VIEW_PREFERENCE_KEY,
	COLLAPSE_TRANSITION_LABELS_PREFERENCE_KEY,
	NEW_EDITOR_DEFAULT_FOR_ALL_WARNING_SEEN_PREFERENCE_KEY,
} from './defaultEditorPreferenceKey.constants.tsx';

import { getUserPreferences } from './getUserPreferences.tsx';
import { useUserPreference, useUserPreferencesInitialiser } from './useUserPreference.tsx';

const workflowUserPreferenceKeys: readonly [
	'jira.user.workflow.editor.global.scope.enrolment',
	'jira.user.workflow.editor.changeboarding-dismissed',
	'jira.user.workflow.editor.list-view',
	'jira.user.workflow.editor.collapsed.transitions',
	'jira.user.workflow.editor.new-editor-default-for-all-warning-seen',
] = [
	DEFAULT_EDITOR_PREFERENCE_KEY,
	CHANGEBOARDING_DISMISSED_PREFERENCE_KEY,
	TEXT_VIEW_PREFERENCE_KEY,
	COLLAPSE_TRANSITION_LABELS_PREFERENCE_KEY,
	NEW_EDITOR_DEFAULT_FOR_ALL_WARNING_SEEN_PREFERENCE_KEY,
] as const;

export const workflowUserPreferencesResource: RouteResource<UserPreferences> =
	createResource<UserPreferences>({
		type: WORKFLOW_USER_PREFERENCES_KEY,
		getKey: () => WORKFLOW_USER_PREFERENCES_KEY,
		getData: () => getUserPreferences([...workflowUserPreferenceKeys]),
		maxAge: 1000,
		isBrowserOnly: true,
	});

type WorkflowUserPreferenceKey = (typeof workflowUserPreferenceKeys)[number];

export const useWorkflowUserPreference = (
	key: WorkflowUserPreferenceKey,
	options?: PreferenceOptions,
): {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	value: any;
	setValue: (newValue: string) => void;
	error: // oxlint-disable-next-line @typescript-eslint/no-explicit-any
		| Record<string, any>
		| Error
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		| (Record<string, any> & Error)
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		| (Error & Record<string, any>)
		| null;
	loading: boolean;
} => useUserPreference(workflowUserPreferencesResource, key, options);

export const useWorkflowUserPreferencesInitialiser = (): {
	initialise: () => void;
} => useUserPreferencesInitialiser(workflowUserPreferencesResource);

export const useCollapseTransitionLabelsPreferenceFromResource = (): {
	value: boolean;
	setValue: (collapsed: boolean) => void;
} => {
	const { value, setValue } = useWorkflowUserPreference(COLLAPSE_TRANSITION_LABELS_PREFERENCE_KEY);

	const areTransitionLabelsCollapsed = !value ? true : value === 'true';

	const setTransitionLabelsCollapsed = useCallback(
		(collapsed: boolean) => {
			setValue(collapsed.toString());
		},
		[setValue],
	);

	return { value: areTransitionLabelsCollapsed, setValue: setTransitionLabelsCollapsed };
};
